export type CronPreviewStatus = 'empty' | 'valid' | 'invalid' | 'unsupported';

export interface CronPreviewResult {
  status: CronPreviewStatus;
  descriptionKey?: string;
  descriptionParams?: Record<string, string | number>;
  errorKey?: string;
  nextRuns: string[];
}

export type ScheduleEditorType = 'minutes' | 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

export interface ScheduleEditorValue {
  type: ScheduleEditorType;
  interval: number;
  hour: number;
  minute: number;
  dayOfWeek: string;
  dayOfMonth: number;
}

export const DEFAULT_SCHEDULE_EDITOR: ScheduleEditorValue = {
  type: 'daily',
  interval: 5,
  hour: 2,
  minute: 0,
  dayOfWeek: 'MON',
  dayOfMonth: 1
};

interface ParsedField {
  values: number[];
  wildcard: boolean;
}

interface ParsedCron {
  seconds: ParsedField;
  minutes: ParsedField;
  hours: ParsedField;
  daysOfMonth: ParsedField;
  months: ParsedField;
  daysOfWeek: ParsedField;
  sourceFields: string[];
}

const MONTH_NAMES: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12
};

const DAY_NAMES: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6
};

const FIELD_ERROR_KEYS = [
  'scheduled-task.cron-error.second',
  'scheduled-task.cron-error.minute',
  'scheduled-task.cron-error.hour',
  'scheduled-task.cron-error.day-of-month',
  'scheduled-task.cron-error.month',
  'scheduled-task.cron-error.day-of-week'
];

const pad = (value: number): string => String(value).padStart(2, '0');

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));

export function parseScheduleEditor(expression: string): ScheduleEditorValue {
  const fields = expression.trim().toUpperCase().split(/\s+/);
  const custom = { ...DEFAULT_SCHEDULE_EDITOR, type: 'custom' as const };
  if (fields.length !== 6) return custom;

  const [second, minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  if (second !== '0' || month !== '*') return custom;

  const intervalMatch = minute.match(/^(?:\*|0)\/(\d+)$/);
  if (intervalMatch && hour === '*' && dayOfMonth === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'minutes', interval: clamp(Number(intervalMatch[1]), 1, 59) };
  }

  if (minute === '*' && hour === '*' && dayOfMonth === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'minutes', interval: 1 };
  }

  if (/^\d+$/.test(minute) && hour === '*' && dayOfMonth === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'hourly', minute: clamp(Number(minute), 0, 59) };
  }

  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour)) return custom;
  const time = {
    hour: clamp(Number(hour), 0, 23),
    minute: clamp(Number(minute), 0, 59)
  };

  if (dayOfMonth === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'daily', ...time };
  }

  if ((dayOfMonth === '?' || dayOfMonth === '*') && (dayOfWeek === 'MON-FRI' || dayOfWeek === '1-5')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'weekdays', ...time };
  }

  if ((dayOfMonth === '?' || dayOfMonth === '*') && /^(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)$/.test(dayOfWeek)) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'weekly', dayOfWeek, ...time };
  }

  if (/^\d+$/.test(dayOfMonth) && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return { ...DEFAULT_SCHEDULE_EDITOR, type: 'monthly', dayOfMonth: clamp(Number(dayOfMonth), 1, 31), ...time };
  }

  return custom;
}

export function composeScheduleEditor(editor: ScheduleEditorValue): string | null {
  const interval = clamp(Math.round(editor.interval), 1, 59);
  const hour = clamp(Math.round(editor.hour), 0, 23);
  const minute = clamp(Math.round(editor.minute), 0, 59);
  const dayOfMonth = clamp(Math.round(editor.dayOfMonth), 1, 31);
  const dayOfWeek = /^(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)$/.test(editor.dayOfWeek.toUpperCase()) ? editor.dayOfWeek.toUpperCase() : 'MON';

  switch (editor.type) {
    case 'minutes':
      return `0 */${interval} * * * ?`;
    case 'hourly':
      return `0 ${minute} * * * ?`;
    case 'daily':
      return `0 ${minute} ${hour} * * ?`;
    case 'weekdays':
      return `0 ${minute} ${hour} ? * MON-FRI`;
    case 'weekly':
      return `0 ${minute} ${hour} ? * ${dayOfWeek}`;
    case 'monthly':
      return `0 ${minute} ${hour} ${dayOfMonth} * ?`;
    default:
      return null;
  }
}

function parseValue(
  value: string,
  names: Record<string, number> | undefined,
  min: number,
  max: number,
  normalizeDay = false
): number | null {
  const namedValue = names?.[value.toUpperCase()];
  const parsed = namedValue ?? (/^\d+$/.test(value) ? Number(value) : Number.NaN);
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return null;
  return normalizeDay && parsed === 7 ? 0 : parsed;
}

function parseField(
  source: string,
  min: number,
  max: number,
  options: { allowQuestion?: boolean; names?: Record<string, number>; normalizeDay?: boolean } = {}
): ParsedField | null {
  const normalized = source.toUpperCase();
  if (normalized === '?' && !options.allowQuestion) return null;

  const wildcard = normalized === '*' || normalized === '?';
  const values = new Set<number>();
  const segments = normalized.split(',');
  if (segments.some(segment => !segment)) return null;

  for (const segment of segments) {
    const stepParts = segment.split('/');
    if (stepParts.length > 2) return null;

    const base = stepParts[0];
    const step = stepParts.length === 2 ? Number(stepParts[1]) : 1;
    if (!Number.isInteger(step) || step <= 0) return null;

    let rangeStart = min;
    let rangeEnd = max;
    if (base !== '*' && base !== '?') {
      const rangeParts = base.split('-');
      if (rangeParts.length > 2) return null;

      const start = parseValue(rangeParts[0], options.names, min, max, options.normalizeDay);
      if (start == null) return null;
      rangeStart = start;

      if (rangeParts.length === 2) {
        const end = parseValue(rangeParts[1], options.names, min, max, options.normalizeDay);
        if (end == null || end < rangeStart) return null;
        rangeEnd = end;
      } else if (stepParts.length === 1) {
        rangeEnd = rangeStart;
      }
    }

    for (let value = rangeStart; value <= rangeEnd; value += step) {
      values.add(options.normalizeDay && value === 7 ? 0 : value);
    }
  }

  return { values: [...values].sort((a, b) => a - b), wildcard };
}

function parseCron(expression: string): { cron?: ParsedCron; errorKey?: string } {
  const sourceFields = expression.trim().split(/\s+/);
  if (sourceFields.length !== 6) return { errorKey: 'scheduled-task.cron-error.field-count' };

  const fieldDefinitions = [
    [0, 59, {}],
    [0, 59, {}],
    [0, 23, {}],
    [1, 31, { allowQuestion: true }],
    [1, 12, { names: MONTH_NAMES }],
    [0, 7, { allowQuestion: true, names: DAY_NAMES, normalizeDay: true }]
  ] as const;

  const parsedFields = sourceFields.map((field, index) => {
    const [min, max, options] = fieldDefinitions[index];
    return parseField(field, min, max, options);
  });
  const invalidIndex = parsedFields.findIndex(field => field == null);
  if (invalidIndex >= 0) return { errorKey: FIELD_ERROR_KEYS[invalidIndex] };

  return {
    cron: {
      seconds: parsedFields[0]!,
      minutes: parsedFields[1]!,
      hours: parsedFields[2]!,
      daysOfMonth: parsedFields[3]!,
      months: parsedFields[4]!,
      daysOfWeek: parsedFields[5]!,
      sourceFields
    }
  };
}

function zonedParts(
  date: Date,
  zoneId: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part => [part.type, Number(part.value)]));
  return {
    year: values['year'],
    month: values['month'],
    day: values['day'],
    hour: values['hour'],
    minute: values['minute'],
    second: values['second']
  };
}

function zonedLocalToDate(
  local: { year: number; month: number; day: number; hour: number; minute: number; second: number },
  zoneId: string
): Date | null {
  const targetTime = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
  let candidate = new Date(targetTime);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = zonedParts(candidate, zoneId);
    const actualTime = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
    const difference = actualTime - targetTime;
    if (difference === 0) return candidate;
    candidate = new Date(candidate.getTime() - difference);
  }

  const actual = zonedParts(candidate, zoneId);
  return Object.entries(local).every(([key, value]) => actual[key as keyof typeof actual] === value) ? candidate : null;
}

function matchesDay(cron: ParsedCron, year: number, month: number, day: number): boolean {
  if (!cron.months.values.includes(month)) return false;

  const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const dayOfMonthMatches = cron.daysOfMonth.values.includes(day);
  const dayOfWeekMatches = cron.daysOfWeek.values.includes(dayOfWeek);

  if (cron.daysOfMonth.wildcard) return dayOfWeekMatches;
  if (cron.daysOfWeek.wildcard) return dayOfMonthMatches;
  return dayOfMonthMatches && dayOfWeekMatches;
}

const WEEKDAY_LABEL_KEYS: Record<string, string> = {
  MON: 'scheduled-task.weekday.mon',
  '1': 'scheduled-task.weekday.mon',
  TUE: 'scheduled-task.weekday.tue',
  '2': 'scheduled-task.weekday.tue',
  WED: 'scheduled-task.weekday.wed',
  '3': 'scheduled-task.weekday.wed',
  THU: 'scheduled-task.weekday.thu',
  '4': 'scheduled-task.weekday.thu',
  FRI: 'scheduled-task.weekday.fri',
  '5': 'scheduled-task.weekday.fri',
  SAT: 'scheduled-task.weekday.sat',
  '6': 'scheduled-task.weekday.sat',
  SUN: 'scheduled-task.weekday.sun',
  '0': 'scheduled-task.weekday.sun',
  '7': 'scheduled-task.weekday.sun'
};

function describeCron(cron: ParsedCron): Pick<CronPreviewResult, 'descriptionKey' | 'descriptionParams'> {
  const [second, minute, hour, dayOfMonth, month, dayOfWeek] = cron.sourceFields.map(field => field.toUpperCase());

  const everySecondMatch = second.match(/^(?:\*|0)\/(\d+)$/);
  if (
    everySecondMatch &&
    minute === '*' &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    const interval = Number(everySecondMatch[1]);
    return {
      descriptionKey: 'scheduled-task.cron-preview.every-seconds',
      descriptionParams: { interval }
    };
  }

  const everyMinuteMatch = minute.match(/^(?:\*|0)\/(\d+)$/);
  const isEveryMinute = (everyMinuteMatch && Number(everyMinuteMatch[1]) === 1) || minute === '*';
  if (
    second === '0' &&
    (everyMinuteMatch || minute === '*') &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    if (isEveryMinute) {
      return {
        descriptionKey: 'scheduled-task.cron-preview.every-1-minute'
      };
    }
    const interval = Number(everyMinuteMatch![1]);
    return {
      descriptionKey: 'scheduled-task.cron-preview.every-minutes',
      descriptionParams: { interval }
    };
  }

  if (second === '0' && minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.hourly'
    };
  }

  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.hourly-at',
      descriptionParams: { minute: pad(Number(minute)) }
    };
  }

  const everyHourMatch = hour.match(/^(?:\*|0)\/(\d+)$/);
  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    everyHourMatch &&
    dayOfMonth === '*' &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    const interval = Number(everyHourMatch[1]);
    const minVal = Number(minute);
    if (minVal === 0) {
      return {
        descriptionKey: 'scheduled-task.cron-preview.every-hour',
        descriptionParams: { interval }
      };
    }
    return {
      descriptionKey: 'scheduled-task.cron-preview.every-hour-at',
      descriptionParams: { interval, minute: pad(minVal) }
    };
  }

  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    /^\d+$/.test(hour) &&
    dayOfMonth === '*' &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.daily-at',
      descriptionParams: { time: `${pad(Number(hour))}:${pad(Number(minute))}` }
    };
  }

  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    /^\d+$/.test(hour) &&
    (dayOfMonth === '?' || dayOfMonth === '*') &&
    month === '*' &&
    (dayOfWeek === 'MON-FRI' || dayOfWeek === '1-5')
  ) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.weekdays-at',
      descriptionParams: { time: `${pad(Number(hour))}:${pad(Number(minute))}` }
    };
  }

  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    /^\d+$/.test(hour) &&
    (dayOfMonth === '?' || dayOfMonth === '*') &&
    month === '*' &&
    WEEKDAY_LABEL_KEYS[dayOfWeek]
  ) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.weekly-at',
      descriptionParams: {
        weekdayKey: WEEKDAY_LABEL_KEYS[dayOfWeek],
        time: `${pad(Number(hour))}:${pad(Number(minute))}`
      }
    };
  }

  if (
    second === '0' &&
    /^\d+$/.test(minute) &&
    /^\d+$/.test(hour) &&
    /^\d+$/.test(dayOfMonth) &&
    month === '*' &&
    (dayOfWeek === '?' || dayOfWeek === '*')
  ) {
    return {
      descriptionKey: 'scheduled-task.cron-preview.monthly-at',
      descriptionParams: {
        dayOfMonth: Number(dayOfMonth),
        time: `${pad(Number(hour))}:${pad(Number(minute))}`
      }
    };
  }

  return { descriptionKey: 'scheduled-task.cron-preview.advanced' };
}

function findNextRuns(cron: ParsedCron, zoneId: string, now: Date, limit: number): Date[] {
  const start = zonedParts(now, zoneId);
  const startDay = Date.UTC(start.year, start.month - 1, start.day);
  const results: Date[] = [];

  for (let dayOffset = 0; dayOffset <= 366 * 5 && results.length < limit; dayOffset += 1) {
    const cursor = new Date(startDay + dayOffset * 86_400_000);
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth() + 1;
    const day = cursor.getUTCDate();
    if (!matchesDay(cron, year, month, day)) continue;

    for (const hour of cron.hours.values) {
      if (dayOffset === 0 && hour < start.hour) continue;
      for (const minute of cron.minutes.values) {
        if (dayOffset === 0 && hour === start.hour && minute < start.minute) continue;
        for (const second of cron.seconds.values) {
          if (dayOffset === 0 && hour === start.hour && minute === start.minute && second <= start.second) continue;
          const candidate = zonedLocalToDate({ year, month, day, hour, minute, second }, zoneId);
          if (!candidate || candidate.getTime() <= now.getTime()) continue;
          results.push(candidate);
          if (results.length === limit) return results;
        }
      }
    }
  }

  return results;
}

function formatRun(date: Date, zoneId: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(date);
}

export function buildSpringCronPreview(expression: string, zoneId: string, now = new Date()): CronPreviewResult {
  const normalized = expression.trim();
  if (!normalized) return { status: 'empty', nextRuns: [] };

  const fields = normalized.split(/\s+/);
  if (fields.length !== 6) {
    return { status: 'invalid', errorKey: 'scheduled-task.cron-error.field-count', nextRuns: [] };
  }

  if (/[LW]/i.test(fields[3]) || /[L#]/i.test(fields[5])) {
    return { status: 'unsupported', nextRuns: [] };
  }

  const parsed = parseCron(normalized);
  if (!parsed.cron) return { status: 'invalid', errorKey: parsed.errorKey, nextRuns: [] };

  try {
    const nextRuns = findNextRuns(parsed.cron, zoneId || 'UTC', now, 5).map(date => formatRun(date, zoneId || 'UTC'));
    return {
      status: 'valid',
      ...describeCron(parsed.cron),
      nextRuns
    };
  } catch {
    return { status: 'unsupported', errorKey: 'scheduled-task.cron-error.time-zone', nextRuns: [] };
  }
}
